import { Link } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";

export type QorumLogoSize = "sm" | "md" | "lg" | "xl";

export interface QorumLogoProps {
  size?: QorumLogoSize;
  className?: string;
  as?: "div" | "h1" | "a";
  href?: string;
  ariaLabel?: string;
  /** "navy" (default, fond clair) ou "ivory" (fond sombre). */
  tone?: "navy" | "ivory";
}

const SIZE_TO_FONT: Record<QorumLogoSize, string> = {
  sm: "28px",
  md: "40px",
  lg: "80px",
  xl: "clamp(64px, 16vw, 240px)",
};

export function QorumLogo({
  size = "md",
  className,
  as = "div",
  href,
  ariaLabel = "Hōjin",
  tone = "navy",
}: QorumLogoProps) {
  const fontSize = SIZE_TO_FONT[size];
  const textColor = tone === "ivory" ? "var(--ivory)" : "var(--navy)";

  const wrapperStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "baseline",
    gap: "0.04em",
    fontFamily: '"Fraunces", serif',
    fontWeight: 900,
    fontSize,
    lineHeight: 1,
    color: textColor,
    fontVariationSettings: '"opsz" 144, "SOFT" 30',
    letterSpacing: "-0.02em",
    textDecoration: "none",
    margin: 0,
  };

  const inner: ReactNode = (
    <span className="qorum-text" style={{ position: "relative", display: "inline-block" }} aria-label="Hōjin">
      H<span className="hojin-o" aria-hidden="true">o</span>jin
    </span>
  );


  if (as === "a") {
    if (href && href.startsWith("/")) {
      return (
        <Link
          to={href}
          aria-label={ariaLabel}
          className={className}
          style={wrapperStyle}
        >
          {inner}
        </Link>
      );
    }
    return (
      <a
        href={href ?? "#"}
        aria-label={ariaLabel}
        className={className}
        style={wrapperStyle}
      >
        {inner}
      </a>
    );
  }

  if (as === "h1") {
    return (
      <h1 aria-label={ariaLabel} className={className} style={wrapperStyle}>
        {inner}
      </h1>
    );
  }

  return (
    <div aria-label={ariaLabel} className={className} style={wrapperStyle}>
      {inner}
    </div>
  );
}
